import { useMutation, UseMutationOptions, useQueryClient } from "@tanstack/react-query";
import { Template } from "@/types";
import { TemplateData } from "@/types/templates";
import { api, UnprocessableEntityError } from "@/utils/api";
import { camelize, underscore } from "@/utils/api-helpers";

export type UpdateTemplateInput = {
  data: TemplateData;
  /**
   * Content sha of the notiflow version the template was loaded from. Sent as
   * `expected_sha` (via `underscore`) for optimistic concurrency; omit to force.
   */
  expectedSha?: string;
};

export const useUpdateTemplate = (
  projectId: string,
  notiflowId: string,
  notiflowHandle: string,
  notiflowStepId: string,
  options: Omit<
    UseMutationOptions<
      Template,
      UnprocessableEntityError,
      UpdateTemplateInput,
      unknown
    >,
    "mutationFn"
  > = {}
) => {
  const queryClient = useQueryClient();

  return useMutation<Template, UnprocessableEntityError, UpdateTemplateInput, unknown>({
    mutationFn: async (input: UpdateTemplateInput): Promise<Template> => {
      const response = await api.patch<Template>(
        `/projects/${projectId}/notiflows/${notiflowId}/steps/${notiflowStepId}/template`,
        underscore(input)
      );
      return camelize(response.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["projects", projectId, "notiflows", notiflowHandle],
      });
      queryClient.invalidateQueries({
        queryKey: ["projects", projectId, "notiflows", notiflowId, "steps", notiflowStepId, "template"],
      });
    },
    ...options
  });
};
